import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../components/ui/Modal';
import MediaStep, { MediaStepRef } from './PropertyForm/MediaStep';
import { PropertyService } from '../../handling_data/services/PropertyService';
import { useUpload } from '../../contexts/UploadContext';
import { Button } from '../../components/ui/Button';
import { Loader2, Save } from 'lucide-react';

interface MediaManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    propertyId: number | null;
}

export const MediaManagementModal: React.FC<MediaManagementModalProps> = ({ isOpen, onClose, propertyId }) => {
    const { t } = useTranslation();
    const { uploadFiles } = useUpload();
    const mediaRef = useRef<MediaStepRef>(null);

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [propertyData, setPropertyData] = useState<any>(null);

    useEffect(() => {
        if (isOpen && propertyId) {
            fetchPropertyMedia();
        } else if (!isOpen) {
            setPropertyData(null);
            setError(null);
        }
    }, [isOpen, propertyId]);

    const fetchPropertyMedia = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await PropertyService.getPropertyImages(propertyId!);
            // Handle both { data: [...] } and direct array responses
            const images = response.data || response;

            // Map the response to the format expected by MediaStep
            // The first image is the main image according to user specification
            const mappedMedia = Array.isArray(images) ? images.map((m: any, index: number) => ({
                id: m.id,
                url: m.url,
                is_main: index === 0
            })) : [];

            setPropertyData({ media: mappedMedia });
        } catch (err: any) {
            console.error('Failed to fetch property images:', err);
            setError(t('common.error_loading') || 'حدث خطأ في تحميل الصور');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!propertyId || !mediaRef.current) return;

        const data = mediaRef.current.getData();
        const hasUploads = (data.mainImageFile || (data.galleryFiles && data.galleryFiles.length > 0));
        const hasDeletions = (data.deletedMediaIds && data.deletedMediaIds.length > 0);

        if (!hasUploads && !hasDeletions) {
            onClose();
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            // Unified Request: Both Uploads and Deletions
            const newFiles: File[] = [];
            if (data.mainImageFile) newFiles.push(data.mainImageFile);
            if (data.galleryFiles?.length > 0) newFiles.push(...data.galleryFiles);

            // Pass deleteIds to trigger the unified API
            await uploadFiles(propertyId, newFiles, 'property_image', data.deletedMediaIds);

            onClose();
        } catch (err: any) {
            console.error('Failed to save media changes:', err);
            setError(err.response?.data?.message || t('common.error_saving') || 'حدث خطأ أثناء حفظ التعديلات');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t('property_form.media') || 'إدارة الصور'}
        >
            <div className="space-y-6">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12 space-y-4">
                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        <p className="text-gray-500 text-sm">{t('common.loading')}</p>
                    </div>
                ) : error ? (
                    <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-center text-sm">
                        {error}
                    </div>
                ) : propertyData ? (
                    <>
                        <MediaStep
                            ref={mediaRef}
                            t={t}
                            mainImageFile={null}
                            galleryFiles={[]}
                            existingImages={propertyData.media || []}
                        />

                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                            <Button
                                variant="outline"
                                onClick={onClose}
                                disabled={submitting}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                onClick={handleSave}
                                isLoading={submitting}
                            >
                                <Save className="w-4 h-4" />
                                {t('common.save_changes')}
                            </Button>
                        </div>
                    </>
                ) : null}
            </div>
        </Modal>
    );
};
